import { useNavigate } from "@tanstack/react-router";
import {
  BarChart3,
  Home,
  Receipt,
  Settings,
  Smartphone,
  Store,
  Users,
  UtensilsCrossed,
} from "lucide-react";
import {
  type ComponentType,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "~/components/ui/command";

interface CommandMenuItem {
  title: string;
  href: string;
  icon: ComponentType<{ className?: string }>;
  group: string;
}

const COMMAND_ITEMS: CommandMenuItem[] = [
  // Overview
  { title: "Dashboard", href: "/", icon: Home, group: "Overview" },

  // Menus & Items
  { title: "Menus", href: "/menus", icon: UtensilsCrossed, group: "Menus" },

  // Orders
  { title: "Orders", href: "/orders", icon: Receipt, group: "Orders" },

  // Locations
  {
    title: "Locations",
    href: "/locations",
    icon: Store,
    group: "Locations",
  },

  // Devices
  { title: "Devices", href: "/devices", icon: Smartphone, group: "Devices" },

  // Employees
  { title: "Employees", href: "/employees", icon: Users, group: "Employees" },

  // Reports
  { title: "Reports", href: "/reports", icon: BarChart3, group: "Reports" },

  // Settings
  {
    title: "Organization Settings",
    href: "/settings/organization",
    icon: Settings,
    group: "Settings",
  },
  {
    title: "Tax Categories",
    href: "/settings/tax-categories",
    icon: Settings,
    group: "Settings",
  },
  {
    title: "Sales Categories",
    href: "/settings/sales-categories",
    icon: Settings,
    group: "Settings",
  },
];

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((prevOpen) => !prevOpen);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  const runCommand = useCallback((callback: () => void | Promise<void>) => {
    setOpen(false);
    void callback();
  }, []);

  // Group items by category
  const groupedItems = useMemo(() => {
    const groups: Record<string, CommandMenuItem[]> = {};
    for (const item of COMMAND_ITEMS) {
      if (!groups[item.group]) {
        groups[item.group] = [];
      }
      groups[item.group]?.push(item);
    }
    return groups;
  }, []);

  return (
    <CommandDialog onOpenChange={setOpen} open={open}>
      <CommandInput placeholder="Type a command or search..." />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        {Object.entries(groupedItems).map(([group, items], index) => (
          <Fragment key={group}>
            {index > 0 && <CommandSeparator />}
            <CommandGroup heading={group}>
              {items.map((item) => (
                <CommandItem
                  key={item.href}
                  onSelect={() => {
                    runCommand(() => navigate({ to: item.href }));
                  }}
                  value={item.title}
                >
                  <item.icon className="mr-2 h-4 w-4" />
                  <span>{item.title}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </Fragment>
        ))}
      </CommandList>
    </CommandDialog>
  );
}
